import get from 'lodash/get';
import { closeChecklistURLs } from 'routes/close/closeURLs';

import { UserAvatar } from 'components/data-display/UserAvatar';

import { useUsersData } from 'hooks/useUsersData';

import { getName } from 'utils/users/config';

import { GenericNotificationItem } from 'layout/inbox-notifications/GenericNotificationItem';
import { getDueDateText } from 'layout/inbox-notifications/close/_closeNotificationUtils';

import type {
  TaskUpdatedNotification,
  TaskUpdatedNotificationFields,
} from 'types/inbox-notifications/close/ChecklistNotifications';

type AssigneeFields = Extract<
  TaskUpdatedNotificationFields,
  `${'preparers' | 'reviewers'}[${number}].user_id`
>;

type AssigneeDueDateFields = Extract<
  TaskUpdatedNotificationFields,
  `${'preparers' | 'reviewers'}[${number}].due_date_time`
>;

export function UpdatedNotificationItem({
  item,
}: {
  item: TaskUpdatedNotification;
}) {
  if (!item.task || !item.old_task) {
    return <div>Notification task not found</div>;
  }
  // * Task update notification only expects one updated field at a time
  const updatedField = item.updated_fields[0] || '';
  const userProperty = updatedField.split('.').pop() as
    | 'user_id'
    | 'due_date_time';

  switch (userProperty) {
    case 'user_id':
      return (
        <AssigneeNotification
          item={item}
          field={updatedField as AssigneeFields}
        />
      );
    case 'due_date_time':
      return (
        <AssigneeDueDateNotification
          item={item}
          field={updatedField as AssigneeDueDateFields}
        />
      );
    default:
      return <div>Unknown updated field: {userProperty}</div>;
  }
}

function AssigneeNotification({
  item,
  field,
}: {
  item: TaskUpdatedNotification;
  field: AssigneeFields;
}) {
  const { usersMap } = useUsersData();
  const actorName = getName(item.actor_id, usersMap);
  const actorType = field.startsWith('preparers') ? 'preparer' : 'reviewer';
  const oldActorName = getName(get(item.old_task, field), usersMap);
  const newActorName = getName(get(item.task, field), usersMap);

  return (
    <GenericNotificationItem
      item={item}
      url={closeChecklistURLs.taskBase({
        taskId: item.task.core_snapshot.short_code,
      })}
      avatar={<UserAvatar userId={item.actor_id} />}
      title={`${actorName} assigned you a task as ${actorType}.`}
      description={`${oldActorName} → ${newActorName}`}
    />
  );
}

function AssigneeDueDateNotification({
  item,
  field,
}: {
  item: TaskUpdatedNotification;
  field: AssigneeDueDateFields;
}) {
  const { usersMap } = useUsersData();
  const actorName = getName(item.actor_id, usersMap);
  const oldActorDueDate = getDueDateText(get(item.old_task, field));
  const newActorDueDate = getDueDateText(get(item.task, field));

  return (
    <GenericNotificationItem
      item={item}
      url={closeChecklistURLs.taskBase({
        taskId: item.task.core_snapshot.short_code,
      })}
      avatar={<UserAvatar userId={item.actor_id} />}
      title={`${actorName} updated the due date of your task.`}
      description={`${oldActorDueDate} → ${newActorDueDate}`}
    />
  );
}
